<template>
  <section class="section">
    <div class="columns is-centered">
      <div class="column is-one-third">
        <div class="has-text-centered" style="margin-bottom: 1rem;">
          <span style="font-size: 1.75rem; font-weight: 700; letter-spacing: -0.5px; color: #3273dc;">Fooshi</span>
        </div>
        <div class="has-text-centered" style="margin-bottom: 1.5rem;">
          <LanguageSelector />
        </div>
        <OnboardingProgress :current-step="1" />
        <h3 class="title is-4 is-spaced is-centered">
          <span>Sign Up</span>
        </h3>
        <div class="field">
          <label class="label">Email</label>
          <p class="control has-icons-left has-icons-right">
            <input
              v-focus
              class="input"
              type="email"
              placeholder="Email"
              autocomplete="email"
              v-model="signUpInfo.email"
              @blur="validateEmail"
              v-bind:class="{
                'is-danger': (errors && !signUpInfo.email) || failureNotification == 'onboarding.signup.invalidEmailAddress',
              }"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-envelope"></i>
            </span>
          </p>
          <p class="help is-danger"
              v-if="failureNotification"
            >
            {{ $t(failureNotification) }}
          </p>
        </div>
        <div class="field">
          <label class="label">Password</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              :type="passwordFieldType"
              placeholder="Password"
              autocomplete="new-password"
              v-model="signUpInfo.password"
              v-bind:class="{
                'is-danger': errors && (!signUpInfo.password || !validPassword),
              }"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-lock"></i>
            </span>
            <span class="icon is-small is-right">
              <i v-bind:class="{
                'fa': true,
                'fa-eye': !showPassword,
                'fa-eye-slash': showPassword
                }"
                style="pointer-events: all; cursor: pointer"
                @click="showHidePassword()">
              </i>
            </span>
          </p>
          <p class="help"
              v-if="errors && !validPassword"
              v-bind:class="{
                'is-danger': errors && !validPassword,
              }"
            >
            {{ $t('onboarding.signup.invalidPassword')}}
          </p>
        </div>
        <div class="field">
          <label class="label">Repeat Password</label>
          <p class="control has-icons-left has-icons-right">
            <input
              class="input"
              :type="passwordFieldType"
              placeholder="Password"
              autocomplete="new-password"
              v-model="signUpInfo.confirmPassword"
              v-bind:class="{
                'is-danger': errors && (!signUpInfo.confirmPassword || signUpInfo.password !== signUpInfo.confirmPassword),
              }"
              v-on:keyup.enter="submit()"
            />
            <span class="icon is-small is-left">
              <i class="fa fa-lock"></i>
            </span>
            <span class="icon is-small is-right">
              <i v-bind:class="{
                'fa': true,
                'fa-eye': !showPassword,
                'fa-eye-slash': showPassword
                }"
                style="pointer-events: all; cursor: pointer"
                @click="showHidePassword()">
              </i>
            </span>
          </p>
          <p class="help"
              v-if="errors && signUpInfo.password !== signUpInfo.confirmPassword"
              v-bind:class="{
                'is-danger': errors && signUpInfo.password !== signUpInfo.confirmPassword
              }"
            >
            {{ $t('onboarding.signup.passwordsMismatch') }}
          </p>
        </div>
        <div class="field">
          <p class="control">
            <primary-button class="is-fullwidth" :label="$t('app.buttons.signup')" @click="submit()" :loading="!ready"></primary-button>
          </p>
        </div>
          <div class="tabs is-centered">
            <ul>
              <li class="is-active">OR</li>
            </ul>
          </div>
          <div class="field">
            <p class="control">
              <button class="button is-info is-fullwidth" @click="logIn()">{{ $t('app.buttons.login')}}</button>
            </p>
          </div>
        </div>
    </div>
  </section>
</template>

<script>
import Auth from '@/auth/auth'
import { FooshiApi } from '@/services/fooshiApi.service'
import { EmailValidator } from '@/services/emailValidator.service'
import PrimaryButton from '../../components/forms/PrimaryButton.vue'
import LanguageSelector from '../../components/LanguageSelector.vue'
import OnboardingProgress from '@/components/OnboardingProgress.vue'

export default {
  components: {
    PrimaryButton,
    LanguageSelector,
    OnboardingProgress
  },
  computed: {
    validPassword () {
      if (this.signUpInfo.password.length < 8) {
        return false
      }
      if (!/\d/.test(this.signUpInfo.password)) {
        return false
      }
      if (!/[a-z]/.test(this.signUpInfo.password)) {
        return false
      }
      if (!/[A-Z]/.test(this.signUpInfo.password)) {
        return false
      }
      if (!/[^0-9a-zA-Z]/.test(this.signUpInfo.password)) {
        return false
      }
      return true
    },
    emailAddress () {
      return this.signUpInfo.email
    }
  },
  methods: {
    validateEmail () {
      if (EmailValidator.isValid(this.signUpInfo.email)) {
        this.failureNotification = ''
      } else {
        this.failureNotification = 'onboarding.signup.invalidEmailAddress'
      }
    },
    async submit () {
      if (
        !this.signUpInfo.email ||
        !this.signUpInfo.password ||
        !this.signUpInfo.confirmPassword ||
        this.signUpInfo.password !== this.signUpInfo.confirmPassword ||
        !this.validPassword
      ) {
        this.errors = true
      } else {
        this.ready = false
        await this.signUp()
        this.ready = true
      }
    },
    async signUp () {
      this.failureNotification = ''
      const response = await FooshiApi.signUp(this.signUpInfo)
      if (response.success) {
        Auth.getInstance().logIn(response)
        if (window.plausible) window.plausible('Signup Completed', { props: { stage: 'acquisition' } })
        this.failureNotification = ''
        this.$router.push('account-details')
      } else {
        if (window.plausible) window.plausible('Signup Failed', { props: { stage: 'acquisition' } })
        this.failureNotification = response.message
      }
    },
    async logIn () {
      this.$router.push('/login')
    },
    showHidePassword () {
      if (this.passwordFieldType === 'password') {
        this.passwordFieldType = 'text'
      } else {
        this.passwordFieldType = 'password'
      }
      this.showPassword = !this.showPassword
    }
  },
  watch: {
    emailAddress (newEmailAddress, oldEmailAddress) {
      // reset error message
      this.failureNotification = ''
    }
  },
  mounted () {
    this.ready = true
  },
  data () {
    return {
      ready: false,
      errors: false,
      failureNotification: '',
      passwordFieldType: 'password',
      showPassword: false,
      signUpInfo: {
        email: '',
        password: '',
        confirmPassword: ''
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/onBoarding/Signup.vue